import { Fragment, useEffect, useState } from 'react'
import { Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, Link, Typography } from '@mui/material'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import CheckIcon from '@mui/icons-material/Check'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import { useNavigate } from 'react-router'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { useScrollTop } from 'hooks/use-scrollTop'
import { BackArrow } from 'Assets/SVGs/back-arrow'
import S3Image from 'Components/S3Image'
import { TryFormatPhoneNumber } from 'core/Utilities'
import { CAREGIVER_PORTAL_DASHBOARD } from 'Pages/Routes'
import { type CareManagementHelpType, type CareManagementProviderOption, type CareManagementServiceOption } from './types'

const HELP_OPTIONS: Array<{ type: CareManagementHelpType, title: string, description: string, icon: JSX.Element }> = [
  { type: 'FindingService', title: 'Help finding a service for my child', description: 'Preschool, childcare, early intervention', icon: <SchoolOutlinedIcon /> },
  { type: 'OtherSupport', title: 'Help finding other support', description: 'Things like food, diapers, housing, or health care', icon: <HomeOutlinedIcon /> },
  { type: 'TalkThrough', title: 'Someone to talk things through with', description: 'General guidance and parenting support', icon: <QuestionMarkIcon /> }
]

const selectedSx = { backgroundColor: '#EFE8FA', border: '1px solid #EFE8FA' }
const unselectedSx = { backgroundColor: '#FFFFFF', border: '1px solid #D9D9D9' }

export function CareManagementSetup (): JSX.Element {
  const nav = useNavigate()
  const [providers, setProviders] = useState<CareManagementProviderOption[] | null>(null)
  const [helpTypes, setHelpTypes] = useState<CareManagementHelpType[]>([])
  const [services, setServices] = useState<CareManagementServiceOption[]>([])
  const [serviceIDs, setServiceIDs] = useState<number[]>([])
  const [isdID, setIsdID] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useScrollTop()

  useEffect(() => {
    async function load (): Promise<void> {
      const [{ response, success }, serviceResult] = await Promise.all([
        sendGet('/CaregiverPortal/GetCareManagementProviderOptions', {}),
        sendGet('/CaregiverPortal/GetCareManagementServiceOptions', {})
      ])
      setServices(serviceResult.success ? serviceResult.response : [])
      const options: CareManagementProviderOption[] = success ? response : []
      setProviders(options)
      // with only one provider there is no choice to make, so it is chosen for them
      if (options.length === 1) {
        setIsdID(options[0].isdid)
      }
    }
    void load()
  }, [])

  const toggleHelp = (type: CareManagementHelpType): void => {
    setHelpTypes(helpTypes.includes(type) ? helpTypes.filter(t => t !== type) : [...helpTypes, type])
    // services only go with asking for help finding one
    if (type === 'FindingService' && helpTypes.includes(type)) {
      setServiceIDs([])
    }
  }

  const toggleService = (id: number): void => {
    setServiceIDs(serviceIDs.includes(id) ? serviceIDs.filter(s => s !== id) : [...serviceIDs, id])
  }

  const confirm = async (): Promise<void> => {
    setSaving(true)
    setError('')
    const { response, success } = await sendPost('/CaregiverPortal/RequestCareManagement', { isdID, helpTypes, serviceIDs })
    setSaving(false)
    if (success && response?.success === true) {
      nav(CAREGIVER_PORTAL_DASHBOARD)
    } else {
      setError(response?.errors?.[0]?.error ?? 'Something went wrong. Please try again.')
    }
  }

  const canConfirm = helpTypes.length > 0 && isdID != null && !saving

  return <Container sx={{ mb: '30px' }}>
    <Box sx={{ mt: '30px', display: 'flex', alignItems: 'center', gap: '16px' }}>
      <Box onClick={() => { nav(CAREGIVER_PORTAL_DASHBOARD) }} sx={{ cursor: 'pointer' }} data-testid='backButton'>
        <BackArrow />
      </Box>
      <Typography variant='h5' sx={{ fontWeight: 600 }} component='h1'>How can we help you?</Typography>
    </Box>

    <Box role='group' aria-label='Kinds of help' sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mt: '20px' }}>
      {HELP_OPTIONS.map(option => {
        const checked = helpTypes.includes(option.type)
        return (
          <Fragment key={option.type}>
            <Box
              role='checkbox'
              aria-checked={checked}
              tabIndex={0}
              data-testid={`helpType-${option.type}`}
              onClick={() => { toggleHelp(option.type) }}
              onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleHelp(option.type) } }}
              sx={{ ...(checked ? selectedSx : unselectedSx), borderRadius: '10px', p: '16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
            >
              <Box sx={{ alignSelf: 'flex-start', color: '#222024' }}>{option.icon}</Box>
              <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
                <Typography sx={{ color: '#222024', fontSize: '16px' }}>{option.title}</Typography>
                <Typography sx={{ color: '#514F53', fontSize: '12px', mt: '8px' }}>{option.description}</Typography>
              </Box>
              {checked && <SelectedCheck />}
            </Box>
            {option.type === 'FindingService' && checked && services.length > 0 && (
              <Box data-testid='serviceOptions' sx={{ ...unselectedSx, borderRadius: '10px', p: '16px', ml: '24px' }}>
                <Typography sx={{ color: '#222024', fontSize: '14px', fontWeight: 600 }} id='serviceOptionsLabel'>
                  Which services are you interested in? (optional)
                </Typography>
                <Box role='group' aria-labelledby='serviceOptionsLabel' sx={{ display: 'flex', flexDirection: 'column' }}>
                  {services.map(service => (
                    <FormControlLabel
                      key={service.id}
                      label={service.name}
                      data-testid={`service-${service.id}`}
                      control={<Checkbox checked={serviceIDs.includes(service.id)} onChange={() => { toggleService(service.id) }} />}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Fragment>
        )
      })}
    </Box>

    {providers == null && <Box sx={{ mt: '24px' }}><CircularProgress /></Box>}
    {providers != null && providers.length > 1 && (
      <Box data-testid='providerChoiceExplanation' sx={{ mt: '20px', p: '16px', borderRadius: '10px', backgroundColor: '#E6F2F1' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2C7A7B' }}>
          <InfoOutlinedIcon fontSize='small' />
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Choose where you want help</Typography>
        </Box>
        <Typography sx={{ color: '#222024', fontSize: '14px', mt: '8px' }}>
          Because your family has addresses in more than one area, you can choose who helps you. Pick whichever works best.
        </Typography>
      </Box>
    )}

    {providers != null && providers.length > 0 && (
      <>
        <Typography sx={{ color: '#222024', fontSize: '18px', fontWeight: 500, mt: '24px', mb: '12px' }} component='h2' id='availableProvidersLabel'>
          Available Providers
        </Typography>
        <Box role='radiogroup' aria-labelledby='availableProvidersLabel' sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {providers.map(provider => {
            const chosen = provider.isdid === isdID
            return (
              <Box
                key={provider.isdid}
                role='radio'
                aria-checked={chosen}
                tabIndex={chosen || (isdID == null && provider === providers[0]) ? 0 : -1}
                data-testid={`provider-${provider.isdid}`}
                // radio behaviour: choosing the chosen provider again keeps it chosen
                onClick={() => { setIsdID(provider.isdid) }}
                onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setIsdID(provider.isdid) } }}
                sx={{
                  ...(chosen ? { ...selectedSx, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' } : unselectedSx),
                  borderRadius: '10px',
                  p: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
                    <Typography sx={{ color: '#222024', fontSize: '18px', fontWeight: 600 }} data-testid='providerName'>{provider.name}</Typography>
                    <Typography sx={{ color: '#8F8F8F', fontSize: '12px' }} data-testid='providerIsd'>{provider.isdName}</Typography>
                  </Box>
                  {chosen && <SelectedCheck />}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {provider.logoS3Key != null && provider.logoS3Key !== '' &&
                    <S3Image imageKey={provider.logoS3Key} alt={provider.name} style={{ width: '24px', height: '24px' }} />}
                  <Typography sx={{ color: '#222024', fontSize: '14px', fontWeight: 600 }}>{provider.name}</Typography>
                </Box>
                {provider.phone != null && provider.phone !== '' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PhoneIcon sx={{ color: '#222024' }} />
                    <Typography sx={{ color: '#222024', fontSize: '14px' }} data-testid='providerPhone'>{TryFormatPhoneNumber(provider.phone)}</Typography>
                  </Box>
                )}
                {provider.email != null && provider.email !== '' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <EmailIcon sx={{ color: '#222024' }} />
                    {/* stopPropagation so following the mail link doesn't also count as choosing the card */}
                    <Link href={`mailto:${provider.email}`} onClick={e => { e.stopPropagation() }} sx={{ color: '#7136BC', fontSize: '14px' }} data-testid='providerEmail'>
                      {provider.email}
                    </Link>
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      </>
    )}

    {providers != null && providers.length === 0 && (
      <Typography sx={{ mt: '24px' }} data-testid='noProviders'>
        There are no care management providers serving your family&apos;s addresses right now.
      </Typography>
    )}

    {error !== '' && <Typography sx={{ color: '#BC3656', mt: '16px' }} data-testid='requestError'>{error}</Typography>}

    <Button
      variant='contained'
      fullWidth
      disabled={!canConfirm}
      onClick={() => { void confirm() }}
      data-testid='confirmButton'
      sx={{ mt: '24px', py: '14px', borderRadius: '32px', fontSize: '16px', fontWeight: 600, textTransform: 'none', backgroundColor: '#7136BC' }}
    >
      Confirm and Continue
    </Button>
  </Container>
}

function SelectedCheck (): JSX.Element {
  return (
    <Box sx={{ backgroundColor: '#0BA5D8', color: '#FFFFFF', borderRadius: '16px', px: '12px', py: '4px', display: 'flex' }}>
      <CheckIcon fontSize='small' />
    </Box>
  )
}
